import { Component, Inject, OnInit } from "@angular/core";
import {
	AbstractControl,
	UntypedFormBuilder,
	UntypedFormGroup,
	ValidationErrors,
	ValidatorFn,
	Validators,
} from "@angular/forms";
import { Entente, EntentePrealable, EntentePrealableLibelle } from "@jack-sparrow-libraries/emotion-models";
import { AppState, GenerateEntentePdf, ToastrAyService } from "features";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { Store } from "@ngrx/store";
import { DateUtils, TypeDate } from "src/app-ay/core/utils/DateUtils";
import { NumberUtils } from "src/app-ay/core/utils/number.utils";

@Component({
	standalone: false,
	selector: "cba-entente-dialog-web-ay",
	templateUrl: "./entente-dialog-web-ay.component.html",
	styleUrls: ["./entente-dialog-web-ay.component.scss"],
})
export class EntenteDialogWebAyComponent implements OnInit {
	today = new Date();
	theForm: UntypedFormGroup;
	min: Date;
	max: Date;
	listActes: string = "";

	codeEntenteOptions = [
		{
			value: EntentePrealable.PAS_DE_REPONSE,
			label: EntentePrealableLibelle.PAS_DE_REPONSE,
		},
		{
			value: EntentePrealable.REPONSE_OK,
			label: EntentePrealableLibelle.REPONSE_OK,
		},
		{
			value: EntentePrealable.NOTION_URGENCE,
			label: EntentePrealableLibelle.NOTION_URGENCE,
		},
		{
			value: EntentePrealable.ACTE_NON_SOUMIS,
			label: EntentePrealableLibelle.ACTE_NON_SOUMIS,
		},
		{ value: EntentePrealable.REFUSEE, label: EntentePrealableLibelle.REFUSEE },
	];

	constructor(
		private fb: UntypedFormBuilder,
		private store: Store<AppState>,
		private toastrAy: ToastrAyService,
		private dialogRef: MatDialogRef<EntenteDialogWebAyComponent>,
		@Inject(MAT_DIALOG_DATA) public data,
	) {}

	ngOnInit() {
		this.theForm = this.fb.group({
			dateEnvoi: [""],
			codeEntente: [""],
			actes: [""],
			commentaireConfidentiel: [""],
			montantPriseCharge: [
				"",
				Validators.compose([
					Validators.pattern("^\\d{0,4}([\\.\\,]\\d{0,2})?$"),
					minEntente(this.data.entente, this.data.smg),
				]),
			],
		});
		if (this.data.smg) {
			this.codeEntenteOptions = [
				{
					value: EntentePrealable.SMG_ATTENTE_REPONSE,
					label: EntentePrealableLibelle.SMG_ATTENTE_REPONSE,
				},
				{
					value: EntentePrealable.SMG_REPONSE_RECUE,
					label: EntentePrealableLibelle.SMG_REPONSE_RECUE,
				},
			];
		}
		this.theForm.patchValue({
			dateEnvoi: DateUtils.formatForDatePicker(
				DateUtils.format(this.data.entente.dateEnvoi),
				TypeDate.CompleteDate,
			),
			codeEntente: this.data.entente.codeEntente,
			commentaireConfidentiel: this.data.entente.commentaireConfidentiel,
			montantPriseCharge: NumberUtils.arrondiMontant(this.data.entente.montantPriseCharge),
		});
		this.max = new Date(this.data.datePremierSoins);
		this.min = new Date(this.data.ordonnance.datePrescription);
		this.dateChg(new Date(this.data.ordonnance.datePrescription));
		this.getListeActesToString();
	}

	async onValidation() {
		if (this.theForm.valid) {
			const frm = this.theForm.value;
			const entente = {
				...this.data.entente,
				dateEnvoi: frm.dateEnvoi,
				codeEntente: frm.codeEntente,
				libelleActes: frm.actes,
				commentaireConfidentiel: frm.commentaireConfidentiel,
				montantPriseCharge: NumberUtils.decimalToEntier(frm.montantPriseCharge),
				montantPriseChargeReel: NumberUtils.decimalToEntier(frm.montantPriseCharge) / 100,
			} as Entente;
			await this.toastrAy.closeToastr();
			this.dialogRef.close({ entente });
		} else {
			this.toastrAy.showToastr();
		}
	}

	getListeActesToString() {
		let initialActesStr = "";
		if (this.data.entente.libelleActes) {
			initialActesStr = this.data.entente.libelleActes;
		} else {
			const actesArray = [];
			for (const traitement of this.data.ordonnanceSoins) {
				const traitementActesArray = traitement.traitementActes.map((acte) => {
					return `${acte.ngapQuantite} ${acte.code} ${acte.ngapCoefficient / 100} (${acte.ngapQuantite} séance${
						acte.ngapQuantite > 1 ? "s" : ""
					})`;
				});

				if (traitementActesArray.length > 0) {
					actesArray.push(traitementActesArray.join(" "));
				}
			}

			initialActesStr = actesArray.join(" - ");
		}

		this.listActes = initialActesStr;
		this.theForm.patchValue({ actes: this.listActes });
	}

	close() {
		this.dialogRef.close();
	}

	dateChg(data) {
		this.theForm.patchValue({ dateEnvoi: data });
	}

	commentaireChg(comm) {
		this.theForm.patchValue({ commentaireConfidentiel: comm.target.value });
	}

	actesChg(input) {
		this.theForm.patchValue({ actes: input.target.value });
	}

	codeEntenteChg(code) {
		this.theForm.patchValue({ codeEntente: code.target.value });
	}

	get commentaireConfidentiel() {
		return this.theForm && this.theForm.get("commentaireConfidentiel");
	}

	async imprimer() {
		if (this.theForm.valid) {
			const frm = this.theForm.value;
			const entente: Entente = {
				...this.data.entente,
				dateEnvoi: frm.dateEnvoi,
				codeEntente: frm.codeEntente,
				libelleActes: frm.actes,
				commentaireConfidentiel: frm.commentaireConfidentiel,
			} as Entente;
			await this.toastrAy.closeToastr();
			this.store.dispatch(new GenerateEntentePdf({ entente, ordonnance: this.data.ordonnance }));
			this.dialogRef.close({ entente });
		} else {
			this.toastrAy.showToastr();
		}
	}
}

export function minEntente(entente: Entente, smg: boolean): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => {
		const v: number = +control.value;

		if (
			smg &&
			(entente.codeEntente === EntentePrealable.REPONSE_OK ||
				entente.codeEntente === EntentePrealable.SMG_REPONSE_RECUE)
		) {
			if (v > 0 && v < entente.montantPriseEnChargeMinimum / 100) {
				return { montantInvalide: true };
			}
		}

		return null;
	};
}
